import { Component, OnDestroy, OnInit } from "@angular/core";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-azure-db-cost-optimization",
  templateUrl: "./azure-db-costoptimization.component.html",
  styleUrls: ["../azure-costoptimization-base.component.css"],
})
export class AzureDbCostOptimizationComponent implements OnInit, OnDestroy {
  pageHeading: string = "DB Recommendations";
  recommendationSummary: string =
    "Recommendations are based on underutilized and idle DB analysis for the selected Azure resource group.";

  userId: string = localStorage.getItem("selectedClientList");
  accountId: string = localStorage.getItem("accountId") || "";
  resourceGroup: string = localStorage.getItem("selectedResourceGroup") || "";
  selectedCloudPartner: string = localStorage.getItem("cp-selected") || "";
  urlPrefix: string =
    localStorage.getItem("role") == "Admin" ? "admin" : "client";

  loading: boolean = false;
  fetching: boolean = true;
  currentMessage: any;
  vmOptimizations: any[] = [];
  jobDetails: any = null;
  loadStatusCount: number = 0;
  destroyed: boolean = false;
  showGlobalLoaderOnNextStatusLoad: boolean = false;

  pages: any = [10, 50, 100, 200];

  currentPage: string = "underutilized";
  currentPagination: any = { page: 1, perPage: 10 };

  recommendations: any = {
    underutilized: [],
    idle: [],
  };

  tabLabels: any = {
    underutilized: "Underutilized",
    idle: "Idle",
  };

  underutilizedHeaders: any[] = [
    { id: "resource_name_status", name: "Resource Name", filter: true },
    { id: "avg_cpu", name: "Avg CPU (%)" },
    { id: "avg_connections", name: "Avg Connections" },
    { id: "avg_io", name: "Avg IO" },
    { id: "current_cost", name: "Current Cost (USD)" },
    { id: "recommended_cost", name: "Recommended Cost (USD)" },
    { id: "Savings", name: "Savings (USD)" },
    { id: "Reason", name: "Reason", filter: true },
    { id: "recommendation", name: "Recommendation", filter: true },
  ];

  idleHeaders: any[] = [
    { id: "resource_name_status", name: "Resource Name", filter: true },
    { id: "avg_cpu", name: "Avg CPU (%)" },
    { id: "avg_connections", name: "Avg Connections" },
    { id: "avg_io", name: "Avg IO" },
    { id: "current_cost", name: "Current Cost (USD)" },
    { id: "recommended_cost", name: "Recommended Cost (USD)" },
    { id: "Savings", name: "Savings (USD)" },
    { id: "Reason", name: "Reason", filter: true },
    { id: "recommendation", name: "Recommendation", filter: true },
  ];

  paginationByTab: any = {
    underutilized: { page: 1, perPage: 10 },
    idle: { page: 1, perPage: 10 },
  };

  constructor(
    private apiService: APIService,
    public notifier: NotifierService
  ) {
    this.currentPagination = this.paginationByTab[this.currentPage];
  }

  ngOnInit(): void {
    this.accountId = localStorage.getItem("accountId") || "";
    this.resourceGroup = localStorage.getItem("selectedResourceGroup") || "";
    this.selectedCloudPartner = localStorage.getItem("cp-selected") || "";

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      let data: any = {};
      try {
        data = JSON.parse(msg);
      } catch (err) {
        return;
      }

      if (data?.value == null) {
        return;
      }

      this.selectedCloudPartner = localStorage.getItem("cp-selected") || "";

      if (data.key == "accountId") {
        this.accountId = data.value;
        this.refresh(true);
      } else if (data.key == "resourceGroup") {
        this.resourceGroup = data.value;
        this.refresh(true);
      }
    });

    this.refresh(true);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  get azureCustomAnalysisBasePath(): string {
    return `${environment.apiURL}/${this.urlPrefix}/azure/costoptimization/cost-optimization/underutilized-rds/custom-analysis`;
  }

  get authHeader(): any {
    return {
      Authorization: localStorage.getItem("t"),
    };
  }

  refresh(showGlobalLoader: boolean = false) {
    if (showGlobalLoader) {
      this.showGlobalLoaderOnNextStatusLoad = true;
    }

    if ((this.selectedCloudPartner || "").toLowerCase() != "azure") {
      this.jobDetails = null;
      this.resetRecommendations();
      this.loading = false;
      this.fetching = false;
      this.showGlobalLoaderOnNextStatusLoad = false;
      return;
    }

    this.currentPage = "underutilized";
    this.currentPagination = this.paginationByTab[this.currentPage];
    this.resetPaginations();
    this.resetRecommendations();
    this.loadStatusCount = 0;
    this.fetching = true;
    this.loadScanStatus();
  }

  setCurrentPage(tab: string) {
    this.currentPage = tab;
    this.currentPagination =
      this.paginationByTab[tab] || this.paginationByTab.underutilized;
  }

  getCurrentRows(): any[] {
    return this.recommendations[this.currentPage] || [];
  }

  getCurrentHeaders(): any[] {
    if (this.currentPage == "idle") {
      return this.idleHeaders;
    }
    return this.underutilizedHeaders;
  }

  getCurrentTabLabel(): string {
    return this.tabLabels[this.currentPage] || "Recommendations";
  }

  getTotalSavings(tab: string = this.currentPage): string {
    const rows: any[] = this.recommendations[tab] || [];

    const total = rows.reduce((sum: number, row: any) => {
      const savingsValue =
        row.Savings ??
        row.estimated_monthly_savings_usd ??
        row.estimated_savings_usd ??
        0;
      return sum + Number(savingsValue || 0);
    }, 0);

    return total.toFixed(2);
  }

  buildQuery(params: any) {
    return Object.keys(params)
      .map((key) => {
        const value = params[key];
        const normalizedValue =
          value === null || value === undefined ? "" : value;
        return `${key}=${encodeURIComponent(normalizedValue)}`;
      })
      .join("&");
  }

  parseApiError(result: any): string {
    if (!result) {
      return "Unknown error";
    }
    if (typeof result == "string") {
      return result;
    }
    return result.error || result.message || "Unknown error";
  }

  formatDateTime(dateString: string): string {
    if (!dateString) {
      return "-";
    }
    const dt = new Date(dateString);
    if (isNaN(dt.getTime())) {
      return dateString;
    }
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  normalizeScanItem(item: any): any {
    return {
      ...item,
      scanned_date_time: item.scanned_date_time,
      scanned_date_time_label: this.formatDateTime(item.scanned_date_time),
      completed_aate_time: item.completed_aate_time || item.completed_date_time,
      completed_aate_time_label: this.formatDateTime(
        item.completed_aate_time || item.completed_date_time
      ),
    };
  }

  private getCreateScanParams(): any {
    return {
      user_id: this.userId,
      subscription_id: this.accountId,
      resource_group_name: this.resourceGroup,
      lookback_days: 30,
      idle_cpu_threshold: 1,
      idle_connection_threshold: 0,
      idle_io_threshold: 1,
      underutilized_cpu_threshold: 20,
      underutilized_connection_threshold: 100,
      underutilized_io_threshold: 20,
    };
  }

  private pickLatestRdsScan(items: any[]): any {
    const rdsItems = (Array.isArray(items) ? items : []).filter((item: any) => {
      const action = (item?.scan_action || item?.action || "")
        .toString()
        .toLowerCase();
      return action == "rds";
    });

    if (rdsItems.length == 0) {
      return null;
    }

    return [...rdsItems]
      .sort((a: any, b: any) => {
        return (
          new Date(b.scanned_date_time).getTime() -
          new Date(a.scanned_date_time).getTime()
        );
      })
      .map((scan: any) => this.normalizeScanItem(scan))[0];
  }

  async loadScanStatus() {
    const showGlobalLoader = this.showGlobalLoaderOnNextStatusLoad;
    if (showGlobalLoader) {
      this.notifier.loading(true);
    }
    this.loading = true;

    try {
      const statusUrl = `${this.azureCustomAnalysisBasePath}/scan-status?${this.buildQuery(
        {
          user_id: this.userId,
          resource_group_name: this.resourceGroup,
          subscription_id: this.accountId,
        }
      )}`;

      const result: any = await this.apiService.getDataPromis(
        statusUrl,
        {},
        this.authHeader
      );

      if (this.destroyed) {
        return;
      }

      const latestScan = this.pickLatestRdsScan(result?.items || []);

      if (result && result.found && latestScan) {
        this.jobDetails = latestScan;

        if ((latestScan.status || "").toLowerCase() == "completed") {
          this.loadStatusCount = 0;
          await this.loadScanDetails(latestScan.scan_id);
        } else {
          this.resetRecommendations();
          if (this.loadStatusCount > 10) {
            this.notifier.alert(
              "Info",
              "",
              "Scan is taking longer than usual. Please click Refresh after a couple of minutes.",
              "info",
              7000
            );
            this.fetching = false;
          } else {
            const currentCount = this.loadStatusCount;
            setTimeout(() => {
              if (this.destroyed || currentCount != this.loadStatusCount) {
                return;
              }
              this.loadStatusCount += 1;
              this.loadScanStatus();
            }, (8 + this.loadStatusCount) * 1000);
          }
        }
      } else {
        this.jobDetails = null;
        this.resetRecommendations();

        if (!(result && !result.found)) {
          const err = this.parseApiError(result);
          if (err.toLowerCase().indexOf("no record") == -1) {
            this.notifier.alert("Error", "", err, "error", 5000);
          }
        }

        this.fetching = false;
      }
    } finally {
      this.loading = false;
      this.showGlobalLoaderOnNextStatusLoad = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
    }
  }

  async loadScanDetails(scanId: string, skipErrorIfPending: boolean = false) {
    if (!scanId) {
      this.fetching = false;
      return;
    }

    const detailsUrl = `${this.azureCustomAnalysisBasePath}/scan-details?${this.buildQuery(
      {
        user_id: this.userId,
        scan_id: scanId,
        subscription_id: this.accountId,
      }
    )}`;

    const result: any = await this.apiService.getDataPromis(
      detailsUrl,
      {},
      this.authHeader
    );

    if (this.destroyed) {
      return;
    }

    const status = (result?.status || "").toString().toLowerCase();
    const hasRows =
      Array.isArray(result?.rds_underutilized) ||
      Array.isArray(result?.rds_idle) ||
      Array.isArray(result?.items);

    if (status == "completed" || hasRows) {
      const underutilizedRows = this.getArrayData(
        result.rds_underutilized,
        result.items
      ).map((item: any) => this.normalizeDbItem(item));

      const idleRows = this.getArrayData(result.rds_idle).map((item: any) =>
        this.normalizeDbItem(item)
      );

      this.recommendations = {
        underutilized: underutilizedRows,
        idle: idleRows,
      };

      this.vmOptimizations = underutilizedRows;

      this.jobDetails = this.normalizeScanItem({
        ...(this.jobDetails || {}),
        scan_id: result.scan_id || this.jobDetails?.scan_id,
        status: result.status || this.jobDetails?.status,
        scanned_date_time:
          result.scanned_date_time || this.jobDetails?.scanned_date_time,
        completed_aate_time:
          result.completed_aate_time || this.jobDetails?.completed_aate_time,
      });

      this.fetching = false;
      return;
    }

    if (skipErrorIfPending && status) {
      this.jobDetails = this.normalizeScanItem({
        ...(this.jobDetails || {}),
        scan_id: result.scan_id || this.jobDetails?.scan_id || scanId,
        status: result.status || this.jobDetails?.status,
        scanned_date_time:
          result.scanned_date_time || this.jobDetails?.scanned_date_time,
        completed_aate_time:
          result.completed_aate_time || this.jobDetails?.completed_aate_time,
      });
      this.fetching = true;
      return;
    }

    this.resetRecommendations();
    const err = this.parseApiError(result);
    if (err.toLowerCase().indexOf("no record") == -1) {
      this.notifier.alert("Error", "", err, "error", 5000);
    }
    this.fetching = false;
  }

  async createScan(showGlobalLoader: boolean = false) {
    if (showGlobalLoader) {
      this.notifier.loading(true);
    }
    this.loading = true;

    try {
      const createScanUrl = `${this.azureCustomAnalysisBasePath}?${this.buildQuery(
        this.getCreateScanParams()
      )}`;

      const result: any = await this.apiService.getDataPromis(
        createScanUrl,
        {},
        this.authHeader
      );

      if (result && (result.scan_id || result.status == "Scan Initiated")) {
        this.notifier.alert(
          "Success",
          "",
          result.message || "Scan initiated successfully",
          "success",
          5000
        );

        this.loadStatusCount = 0;
        this.fetching = true;
        this.resetRecommendations();
        this.jobDetails = this.normalizeScanItem(result);

        if (result.scan_id) {
          await this.loadScanDetails(result.scan_id, true);
          if ((this.jobDetails?.status || "").toLowerCase() != "completed") {
            setTimeout(() => {
              if (!this.destroyed) {
                this.loadScanStatus();
              }
            }, 2000);
          }
        } else {
          setTimeout(() => {
            if (!this.destroyed) {
              this.loadScanStatus();
            }
          }, 2000);
        }
      } else {
        this.notifier.alert(
          "Error",
          "",
          this.parseApiError(result),
          "error",
          5000
        );
      }
    } catch (err: any) {
      this.notifier.alert("Error", "", err?.toString(), "error", 5000);
    } finally {
      this.loading = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
    }
  }

  getCellValue(item: any, key: string): any {
    const value = item ? item[key] : null;
    return value === null || value === undefined || value === "" ? "-" : value;
  }

  private resetRecommendations() {
    this.recommendations = {
      underutilized: [],
      idle: [],
    };
    this.vmOptimizations = [];
  }

  private resetPaginations() {
    this.paginationByTab = {
      underutilized: {
        page: 1,
        perPage: this.paginationByTab.underutilized.perPage || 10,
      },
      idle: { page: 1, perPage: this.paginationByTab.idle.perPage || 10 },
    };
  }

  private getArrayData(primary: any, fallback: any[] = []): any[] {
    if (Array.isArray(primary)) {
      return primary;
    }
    if (Array.isArray(fallback)) {
      return fallback;
    }
    return [];
  }

  private normalizeDbItem(item: any): any {
    return {
      ...item,
      resource_name_status:
        item.resource_name_status ||
        item.db_name ||
        item.server_name ||
        item.resource_name ||
        item.name ||
        item.resource_id,
      avg_cpu: item.avg_cpu ?? item.avg_cpu_percent ?? item.cpu_percent,
      avg_connections:
        item.avg_connections ??
        item.avg_connection ??
        item.connection_count ??
        item.avg_connection_count,
      avg_io:
        item.avg_io ??
        item.avg_iops ??
        item.io_percent ??
        item.avg_io_percent,
      current_cost:
        item.current_cost ?? item.current_monthly_cost_usd ?? item.on_demand_cost,
      recommended_cost: item.recommended_cost,
      Savings:
        item.Savings ??
        item.estimated_monthly_savings_usd ??
        item.estimated_savings_usd,
      Reason: item.Reason || item.reason,
    };
  }
}
